/**
 * CWR 2.4 — pure display helpers for the service/event detail page.
 *
 * Timezone is EXPLICIT everywhere (unlike the catalog's implicit-browser-tz
 * helpers): the page resolves ONE `clientTimezone` (browser/device tz, per the
 * CLAUDE.md "clients use browser tz" convention) and threads it through the
 * picker AND the event "When" so every client-facing time is consistent.
 */
import type { HubServiceSlot, HubServiceDetailDto } from '@/api/types';
import { virtualPlatformLabel } from '@telify/shared';
import { formatDollarAmount } from '@/utils/formatPrice';

/** The one client timezone used for all client-facing times on these screens. */
export function getClientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function localHour(iso: string, tz: string): number {
  const h = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hourCycle: 'h23',
    timeZone: tz,
  }).format(new Date(iso));
  return parseInt(h, 10);
}

/** "6:30 PM" in the given tz. */
export function formatSlotTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  }).format(new Date(iso));
}

export type SlotPeriod = 'morning' | 'afternoon' | 'evening';
export interface PeriodGroup {
  period: SlotPeriod;
  label: string;
  slots: HubServiceSlot[];
}

/**
 * Bucket AVAILABLE slots into Morning (<12:00) · Afternoon (12:00–16:59) ·
 * Evening (≥17:00) by their tz-local hour. Empty groups are dropped.
 */
export function bucketSlots(slots: HubServiceSlot[], tz: string): PeriodGroup[] {
  const groups: Record<SlotPeriod, HubServiceSlot[]> = { morning: [], afternoon: [], evening: [] };
  for (const slot of slots) {
    if (!slot.available) continue;
    const h = localHour(slot.startsAt, tz);
    if (h < 12) groups.morning.push(slot);
    else if (h < 17) groups.afternoon.push(slot);
    else groups.evening.push(slot);
  }
  const order: Array<[SlotPeriod, string]> = [
    ['morning', 'Morning'],
    ['afternoon', 'Afternoon'],
    ['evening', 'Evening'],
  ];
  return order
    .filter(([period]) => groups[period].length > 0)
    .map(([period, label]) => ({ period, label, slots: groups[period] }));
}

// ── Date-strip helpers (calendar-date math, tz-stable) ───────────────────────

function ymdInTz(date: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Today's calendar date (yyyy-MM-dd) in the given tz. */
export function todayYmd(tz: string): string {
  return ymdInTz(new Date(), tz);
}

/** The calendar date (yyyy-MM-dd) of an ISO instant, in the given tz. */
export function ymdFromIso(iso: string, tz: string): string {
  return ymdInTz(new Date(iso), tz);
}

function ymdToNoonUtc(ymd: string): Date {
  return new Date(`${ymd}T12:00:00Z`);
}

/** Add `n` calendar days to a yyyy-MM-dd (noon-UTC anchored — DST-safe). */
export function addDaysYmd(ymd: string, n: number): string {
  const d = ymdToNoonUtc(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "yyyy-MM" of a yyyy-MM-dd. */
export function monthKey(ymd: string): string {
  return ymd.slice(0, 7);
}

/** The "yyyy-MM" of the month AFTER the given yyyy-MM-dd's month. */
export function nextMonthKey(ymd: string): string {
  const [y, m] = ymd.slice(0, 7).split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/**
 * `count` consecutive "yyyy-MM" keys starting at `ymd`'s month — e.g.
 * monthKeysFrom("2026-11-20", 4) → ["2026-11","2026-12","2027-01","2027-02"].
 * Pure integer math (NOT string surgery / chained nextMonthKey) so Dec→Jan year
 * rollovers can't drift. Used to fetch enough months to cover the 90-day horizon.
 */
export function monthKeysFrom(ymd: string, count: number): string[] {
  const [y, m] = ymd.slice(0, 7).split('-').map(Number);
  const keys: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const offset = m - 1 + i; // zero-based month index from Jan of year y
    const yy = y + Math.floor(offset / 12);
    const mm = (offset % 12) + 1;
    keys.push(`${yy}-${String(mm).padStart(2, '0')}`);
  }
  return keys;
}

/** The chronologically-latest yyyy-MM-dd in the set (lexical max == date max for
 *  zero-padded ISO dates), or null if empty. */
export function maxYmd(dates: Iterable<string>): string | null {
  let max: string | null = null;
  for (const d of dates) if (max === null || d > max) max = d;
  return max;
}

/** The chronologically-EARLIEST yyyy-MM-dd in the set (lexical min == date min for
 *  zero-padded ISO dates), or null if empty. */
export function minYmd(dates: Iterable<string>): string | null {
  let min: string | null = null;
  for (const d of dates) if (min === null || d < min) min = d;
  return min;
}

/** Last calendar day of a yyyy-MM-dd's month, as yyyy-MM-dd (UTC-stable). */
function endOfMonthYmd(ymd: string): string {
  const [y, m] = ymd.slice(0, 7).split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); // m is 1-based → day 0 of next month
}

export interface DateChip {
  ymd: string;
  dow: string;
  dnum: string;
  available: boolean;
  /** Full month name, e.g. "June" — drives the sticky month spine. */
  monthLabel: string;
  /** "yyyy-MM" — groups chips into month sections. */
  monthKey: string;
}

/**
 * Build the contiguous date strip: `startYmd` (default today) → `endYmd` (the last
 * bookable day), each day enabled iff it is in `available` (the union of the fetched
 * months' available dates). `startYmd` lets the FIRST bookable day sit flush-left
 * (no greyed lead-in of unavailable days), but is clamped to never start before today.
 * When `endYmd` is null/undefined/past the start, fall back to the end of the start's
 * month. A hard 95-day cap guards the 90-day max horizon. Day + month labels are
 * tz-stable (noon-UTC) so they never drift.
 */
export function buildDateStrip(
  tz: string,
  available: Set<string>,
  endYmd?: string | null,
  startYmd?: string | null,
): DateChip[] {
  const today = todayYmd(tz);
  const start = startYmd && startYmd >= today ? startYmd : today;
  const end = endYmd && endYmd >= start ? endYmd : endOfMonthYmd(start);
  const chips: DateChip[] = [];
  let cur = start;
  for (let i = 0; i < 95 && cur <= end; i += 1) {
    const d = ymdToNoonUtc(cur);
    chips.push({
      ymd: cur,
      dow: new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(d),
      dnum: String(d.getUTCDate()),
      monthLabel: new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(d),
      monthKey: cur.slice(0, 7),
      available: available.has(cur),
    });
    cur = addDaysYmd(cur, 1);
  }
  return chips;
}

/** Short tz label for the picker chip, e.g. "EDT". */
export function tzAbbrev(tz: string, ref: Date = new Date()): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(ref)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

/**
 * The picker chip's label for the day it shows (picker-zone-label-dst): the zone in force at that
 * day's first listed time, else at that day's midday, else now. Read from today, a day past a clock
 * change got today's abbreviation.
 */
export function dayTzLabel(tz: string, ymd: string | null, slots: readonly HubServiceSlot[], now: Date = new Date()): string {
  const ref = slots[0] ? new Date(slots[0].startsAt) : ymd ? new Date(`${ymd}T12:00:00Z`) : now;
  return tzAbbrev(tz, ref);
}

// ── Price / location / duration ──────────────────────────────────────────────

/** Service price for the stats card + sticky bar: "Free" / "$150" / "from $125". */
export function servicePriceText(pricing: HubServiceDetailDto['pricing']): string {
  if (pricing.kind === 'FREE' || pricing.amount == null) return 'Free';
  return pricing.kind === 'FROM' ? `from ${formatDollarAmount(pricing.amount)}` : formatDollarAmount(pricing.amount);
}

/** Event price (single value): null/≤0 → "Free"; else "$30". */
export function eventPriceText(price: number | null): string {
  if (price == null || price <= 0) return 'Free';
  return formatDollarAmount(price);
}

export function formatDuration(min: number): string {
  return `${min} min`;
}

// ── Payment terms (the one state→copy resolver, shared by both detail pages) ──

/**
 * The payment fields the terms line reads. Structurally satisfied by BOTH the
 * service detail `payment` block and the event detail `service` block — neither
 * page derives copy locally (D4: one resolver owns the contract).
 */
export interface PaymentTermsInput {
  paymentRequirement: 'NONE' | 'PAY_AT_SESSION' | 'DEPOSIT' | 'FULL_PAYMENT';
  depositAmount: number | null;
  depositType: 'FIXED' | 'PERCENTAGE' | null;
  depositPercentage: number | null;
  balanceCollectionMethod: 'AT_SESSION' | 'BEFORE_SESSION' | null;
  balanceDueByMinutesBefore: number | null;
}

/** Deposit due-now clause; remainder timing belongs to the future checkout. */
function depositDueTodayClause(p: PaymentTermsInput, priceAmount: number | null): string {
  if (p.depositType === 'PERCENTAGE' && p.depositPercentage != null) {
    const dueToday = priceAmount == null
      ? null
      : formatDollarAmount(priceAmount * (p.depositPercentage / 100));
    return dueToday
      ? `${p.depositPercentage}% deposit · ${dueToday} due today`
      : `${p.depositPercentage}% deposit · due today`;
  }
  if (p.depositType === 'FIXED' && p.depositAmount != null) {
    return `${formatDollarAmount(p.depositAmount)} deposit · due today`;
  }
  return 'Deposit · due today';
}

/**
 * The sticky-bar terms line for an offering, or null (no line). `paid` is derived
 * from each PAGE's setting plus its canonical price source (service: setting not
 * NONE and `pricing.kind !== 'FREE' && amount != null`; event: setting not NONE
 * and `price > 0`) — never a raw-price check here (D4). A free offering shows no
 * terms, and a Free (NONE) offering is free whatever price is still stored
 * (free-means-free, Gabriel 2026-10-12).
 */
export function paymentTermsText(
  payment: PaymentTermsInput,
  paid: boolean,
  priceAmount: number | null = null,
): string | null {
  if (!paid) return null;
  switch (payment.paymentRequirement) {
    case 'FULL_PAYMENT':
      return 'Pay online to book';
    case 'PAY_AT_SESSION':
      return 'Pay at the session';
    case 'NONE':
      return null;
    case 'DEPOSIT':
      return depositDueTodayClause(payment, priceAmount);
  }
}

/** Location row: in-person → name + "City, ST"; virtual → "Online" + platform; by phone → "By phone" alone. */
export function formatLocation(loc: HubServiceDetailDto['location']): { t1: string; t2: string } {
  if (loc.isByPhone) return { t1: 'By phone', t2: '' };
  if (loc.isVirtual) {
    // The platform's display name, never the stored key; Other has none.
    return { t1: 'Online', t2: virtualPlatformLabel(loc.virtualPlatform) ?? '' };
  }
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  return { t1: loc.name ?? 'In person', t2: cityState };
}

/** "Sat, Jun 21 · 10:00 AM" in the given tz (event "When"). */
export function formatWhen(iso: string, tz: string): string {
  const d = new Date(iso);
  const wd = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz }).format(d);
  const md = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: tz }).format(d);
  const tm = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  }).format(d);
  return `${wd}, ${md} · ${tm}`;
}
